/**
 * Project Leaders Service (#399 + delegación de aprobación de compras)
 *
 * Carril web de `project_leaders`. Hasta ahora la tabla NO tenía pantalla:
 * la "configuración por proyecto" que pidió Jorge —dónde el admin marca qué
 * líder puede aprobar compras— solo se podía hacer hablándole al agente.
 *
 * Patrón, igual que `purchaseOrderService`:
 *   - READ  → Supabase directo (RLS ya filtra por rol)
 *   - WRITE → tool del mcp-server, para que el audit log y las validaciones
 *             salgan del MISMO código que usa el agente.
 *
 * En particular, la decisión de "¿quién puede delegar?" NO se reimplementa
 * acá. Vive en tres capas que ya existen:
 *   1. `set_project_leader_purchase_approval` (mcp-server) — admin-only.
 *   2. El trigger `trg_purchase_delegation_admin_only` — vale para todo carril.
 *   3. `can_approve_purchase()` + las policies de `purchases`.
 * La UI solo decide qué PINTAR. Que se equivoque no abre nada.
 *
 * @module services/projectLeaderService
 */

import { supabase } from './supabase';
import { callMcpTool } from './mcpClient';

/**
 * Columna añadida por la migración 20260728120000. Se aísla en una constante
 * porque el SELECT tiene que poder degradarse si esa migración todavía no
 * está aplicada al entorno contra el que corre la app (ver `listProjectLeaders`).
 */
const DELEGATION_COLUMN = 'can_approve_purchases';

const LEADER_EMBEDS = `
  leader:profiles!project_leaders_user_id_fkey(id, full_name, role),
  assigned_by_profile:profiles!project_leaders_assigned_by_fkey(id, full_name)
`;

const BASE_COLUMNS = 'id, project_id, user_id, assigned_at, notes';

/**
 * ¿El error de PostgREST es "esa columna no existe"?
 *
 * Se usa para distinguir "la migración de delegación no está aplicada acá"
 * de un fallo real. Sin esto, un entorno sin la migración mostraría la
 * pantalla entera rota en vez de la lista de líderes + un aviso.
 * PostgREST devuelve 42703 (undefined_column) de Postgres.
 */
const isMissingDelegationColumn = (error) =>
  error?.code === '42703' ||
  /column .*can_approve_purchases.* does not exist/i.test(error?.message || '');

/**
 * Líderes asignados a un proyecto.
 *
 * @param {string} projectId
 * @returns {Promise<{data: Array, delegationAvailable: boolean, error: Error|null}>}
 *   `delegationAvailable: false` significa que la base de este entorno todavía
 *   no tiene `project_leaders.can_approve_purchases` — la lista se puede leer
 *   igual, pero el interruptor no se puede ofrecer.
 */
export const listProjectLeaders = async (projectId) => {
  if (!projectId) {
    return { data: [], delegationAvailable: false, error: new Error('projectId is required') };
  }

  const run = (columns) =>
    supabase
      .from('project_leaders')
      .select(`${columns}, ${LEADER_EMBEDS}`)
      .eq('project_id', projectId)
      .order('assigned_at', { ascending: true });

  try {
    const { data, error } = await run(`${BASE_COLUMNS}, ${DELEGATION_COLUMN}`);

    if (error) {
      if (!isMissingDelegationColumn(error)) throw error;

      // Degradación explícita: se lee sin la columna y se avisa arriba.
      const { data: plain, error: plainError } = await run(BASE_COLUMNS);
      if (plainError) throw plainError;

      return {
        data: (plain || []).map((row) => ({ ...row, can_approve_purchases: false })),
        delegationAvailable: false,
        error: null,
      };
    }

    return { data: data || [], delegationAvailable: true, error: null };
  } catch (error) {
    console.error('listProjectLeaders error:', error);
    return { data: [], delegationAvailable: false, error };
  }
};

/**
 * Enciende o apaga, para UN proyecto, el permiso de un líder de aprobar las
 * compras de ese proyecto.
 *
 * Pasa por la tool del mcp-server (`set_project_leader_purchase_approval`) en
 * vez de hacer un UPDATE directo: esa tool ya valida que el actor sea admin,
 * que la asignación exista, y escribe el audit log
 * (`grant_purchase_approval` / `revoke_purchase_approval`). Duplicar eso acá
 * sería crear un segundo lugar donde la regla puede quedar vieja.
 *
 * @param {{projectId: string, userId: string, canApprovePurchases: boolean}} params
 * @returns {Promise<{data: any, message: string|null, error: Error|null}>}
 */
export const setProjectLeaderPurchaseApproval = async ({
  projectId,
  userId,
  canApprovePurchases,
}) =>
  callMcpTool('set_project_leader_purchase_approval', {
    project_id: projectId,
    user_id: userId,
    can_approve_purchases: canApprovePurchases,
  });

const projectLeaderService = {
  listProjectLeaders,
  setProjectLeaderPurchaseApproval,
};

export default projectLeaderService;
